// ============================================
// Notification Store (Zustand)
// TDL_v1.0 - Task & Backup Notifications
// ============================================

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { format } from 'date-fns'
import type { AppNotification, NotificationType, TaskAlertPreferences } from '@/lib/types'
import { getOverdueTasks, getTasksByDate } from '@/services/database'
// showBrowserNotification 제거 — FCM 푸시로 통합

// Constants
const MAX_NOTIFICATIONS = 50
const BACKUP_REMINDER_DAYS = 7
const NOTIFICATION_EXPIRY_DAYS = 7

// Settings 캐시 — addNotification() 내 동일 키 localStorage 반복 파싱 방지 (1초 TTL)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _settingsCache: { data: any; ts: number } = { data: null, ts: 0 }
const SETTINGS_CACHE_TTL = 1000

function getCachedSettings(): Record<string, any> | null {
  const now = Date.now()
  if (now - _settingsCache.ts < SETTINGS_CACHE_TTL) {
    return _settingsCache.data
  }
  try {
    const raw = localStorage.getItem('todo-settings')
    const settings = raw ? JSON.parse(raw)?.state?.settings : null
    _settingsCache = { data: settings, ts: now }
    return settings
  } catch {
    _settingsCache = { data: null, ts: now }
    return null
  }
}

// Get notification preferences from settings store (Zustand 직접 접근)
function isNotificationEnabled(type: NotificationType): boolean {
  const settings = getCachedSettings()

  // 작업 알림 타입
  const taskAlertMap: Record<string, keyof TaskAlertPreferences> = {
    'task-due': 'dueReminder',
    'task-overdue': 'overdueAlert',
    'task-reminder': 'dueReminder',
  }
  if (type in taskAlertMap) {
    return settings?.alertPreferences?.task?.[taskAlertMap[type]] ?? true
  }

  // 공유 그룹 알림 타입
  const sharedGroupMap: Record<string, string> = {
    'chat-message': 'chatMessage',
    'chat-mention': 'chatMention',
    'comment-mention': 'chatMention',
    'shared-task-created': 'taskAssigned',
    'shared-task-completed': 'taskCompleted',
    'member-joined': 'memberJoined',
  }
  if (type in sharedGroupMap) {
    return settings?.sharedGroupNotifications?.[sharedGroupMap[type]] ?? true
  }

  // backup-reminder, streak-milestone 등 항상 활성
  return true
}

// Generate unique notification key for deduplication
function getNotificationKey(type: NotificationType, taskId?: number, groupId?: string): string {
  if (groupId) return `${type}-${groupId}`
  return `${type}-${taskId || 'global'}`
}

interface NotificationState {
  notifications: AppNotification[]
  lastTriggered: Record<string, number>
  latestNotification: AppNotification | null
  toastQueue: AppNotification[]

  // Core actions
  addNotification: (notification: Omit<AppNotification, 'id' | 'createdAt' | 'read'>) => void
  markAsRead: (id: string) => void
  markAllAsRead: () => void
  removeNotification: (id: string) => void
  clearAll: () => void
  cleanupExpired: () => void
  dequeueToast: () => void

  // Trigger checks
  checkTaskDueNotifications: () => Promise<void>
  checkOverdueNotifications: () => Promise<void>
  checkBackupReminder: (lastBackupDate?: string) => void
}

export const useNotificationStore = create<NotificationState>()(
  devtools(
    persist(
      (set, get) => ({
        notifications: [],
        lastTriggered: {},
        latestNotification: null,
        toastQueue: [],

        addNotification: (notification) => {
          // Check if this notification type is enabled
          if (!isNotificationEnabled(notification.type)) {
            return
          }

          const key = getNotificationKey(notification.type, notification.taskId, notification.groupId)
          const now = Date.now()

          // Check cooldown — 작업 알림만 날짜 기반 쿨다운 (채팅 알림은 즉시 통과)
          const isTaskNotif = notification.type === 'task-due' || notification.type === 'task-overdue'
            || notification.type === 'task-reminder' || notification.type === 'backup-reminder'
          if (isTaskNotif) {
            const lastTrigger = get().lastTriggered[key]
            if (lastTrigger) {
              const lastDate = format(new Date(lastTrigger), 'yyyy-MM-dd')
              const todayDate = format(new Date(now), 'yyyy-MM-dd')
              if (lastDate === todayDate) return
            }
          }

          // Skip if unread notification with same key exists (작업 알림만 — 채팅은 매번 표시)
          if (isTaskNotif) {
            const existing = get().notifications.find(
              (n) => !n.read && getNotificationKey(n.type, n.taskId, n.groupId) === key
            )
            if (existing) return
          }

          const newNotification: AppNotification = {
            ...notification,
            id: crypto.randomUUID(),
            createdAt: new Date(),
            read: false,
          }

          // 알림 센터 + 토스트 모두 표시 (chat-message 포함)
          const skipStore = false
          // 저긴급 알림: 알림 센터에만 기록, 토스트 미표시
          const skipToast = notification.type === 'member-joined'
            || notification.type === 'shared-task-completed'

          set((state) => {
            const hasActiveToast = state.latestNotification !== null

            if (skipStore) {
              // chat-message: 토스트만, notifications 미저장
              return skipToast ? {} : {
                ...(hasActiveToast
                  ? { toastQueue: [...state.toastQueue, newNotification] }
                  : { latestNotification: newNotification }),
              }
            }

            const updated = [newNotification, ...state.notifications]
            const trimmed = updated.length > MAX_NOTIFICATIONS ? updated.slice(0, MAX_NOTIFICATIONS) : updated

            if (skipToast) {
              // member-joined, shared-task-completed: 알림 센터만, 토스트 없음
              return {
                notifications: trimmed,
                lastTriggered: { ...state.lastTriggered, [key]: now },
              }
            }

            return {
              notifications: trimmed,
              lastTriggered: { ...state.lastTriggered, [key]: now },
              ...(hasActiveToast
                ? { toastQueue: [...state.toastQueue, newNotification] }
                : { latestNotification: newNotification }),
            }
          })

          // 사이드이펙트: 소리 + 진동 + 브라우저 알림 (상태 변경 아님 — set 밖에서 실행)
          const settings = getCachedSettings()
          if (!skipToast) {
            try {
              if (settings?.notificationSoundEnabled !== false) {
                const audio = new Audio('/sounds/notification.wav')
                audio.volume = 0.5
                audio.play().catch(() => {})
              }
              // 사용자 제스처 이전 호출은 브라우저가 차단(콘솔 에러) — userActivation 게이트
              if (navigator.vibrate && navigator.userActivation?.hasBeenActive) {
                navigator.vibrate([100, 50, 100])
              }
            } catch { /* 소리/진동 실패 무시 */ }
          }

          // 브라우저 Notification API 제거 — FCM 푸시가 모든 상태를 커버
          // 앱 포커스 중에는 인앱 토스트가 표시됨
        },

        markAsRead: (id) => {
          set((state) => ({
            notifications: state.notifications.map((n) =>
              n.id === id ? { ...n, read: true } : n
            ),
          }))
        },

        markAllAsRead: () => {
          set((state) => ({
            notifications: state.notifications.map((n) => ({ ...n, read: true })),
          }))
        },

        removeNotification: (id) => {
          set((state) => ({
            notifications: state.notifications.filter((n) => n.id !== id),
          }))
        },

        clearAll: () => {
          const now = Date.now()
          const updatedLastTriggered: Record<string, number> = {}
          for (const n of get().notifications) {
            updatedLastTriggered[getNotificationKey(n.type, n.taskId, n.groupId)] = now
          }
          set({ notifications: [], lastTriggered: updatedLastTriggered })
        },

        cleanupExpired: () => {
          const expiryMs = NOTIFICATION_EXPIRY_DAYS * 24 * 60 * 60 * 1000
          const now = Date.now()

          set((state) => {
            const validNotifications = state.notifications.filter((n) => {
              const createdTime = new Date(n.createdAt).getTime()
              return now - createdTime < expiryMs
            })

            const validLastTriggered: Record<string, number> = {}
            for (const [key, timestamp] of Object.entries(state.lastTriggered)) {
              if (now - timestamp < expiryMs) {
                validLastTriggered[key] = timestamp
              }
            }

            return {
              notifications: validNotifications,
              lastTriggered: validLastTriggered,
            }
          })
        },

        dequeueToast: () => {
          const queue = get().toastQueue
          if (queue.length > 0) {
            set({ latestNotification: queue[0], toastQueue: queue.slice(1) })
          } else {
            set({ latestNotification: null })
          }
        },

        checkTaskDueNotifications: async () => {
          const { addNotification } = get()

          try {
            const today = format(new Date(), 'yyyy-MM-dd')
            const tasksDueToday = await getTasksByDate(today)
            const pendingDueToday = tasksDueToday.filter((t) => t.status === 'pending')

            for (const task of pendingDueToday) {
              addNotification({
                type: 'task-due',
                title: '오늘 마감',
                message: `"${task.title}" 할 일이 오늘 마감입니다.`,
                taskId: task.id,
              })
            }
          } catch {
            // Error checking task due notifications
          }
        },

        checkOverdueNotifications: async () => {
          const { addNotification } = get()

          try {
            const overdueTasks = await getOverdueTasks()

            for (const task of overdueTasks) {
              addNotification({
                type: 'task-overdue',
                title: '기한 초과',
                message: `"${task.title}" 할 일이 기한을 초과했습니다. (마감: ${task.dueDate})`,
                taskId: task.id,
              })
            }
          } catch {
            // Error checking overdue notifications
          }
        },

        checkBackupReminder: (lastBackupDate) => {
          const { addNotification } = get()

          if (!lastBackupDate) {
            addNotification({
              type: 'backup-reminder',
              title: '백업 알림',
              message: '데이터 백업을 한 번도 하지 않았습니다. 백업을 권장합니다.',
            })
            return
          }

          const daysSinceBackup = Math.floor(
            (Date.now() - new Date(lastBackupDate).getTime()) / (1000 * 60 * 60 * 24)
          )

          if (daysSinceBackup >= BACKUP_REMINDER_DAYS) {
            addNotification({
              type: 'backup-reminder',
              title: '백업 알림',
              message: `마지막 백업 후 ${daysSinceBackup}일이 경과했습니다.`,
            })
          }
        },
      }),
      {
        name: 'todo-notifications',
        storage: {
          getItem: (name) => {
            const str = localStorage.getItem(name)
            if (!str) return null

            const data = JSON.parse(str)
            if (data.state?.notifications) {
              data.state.notifications = data.state.notifications.map(
                (n: AppNotification) => ({
                  ...n,
                  createdAt: new Date(n.createdAt),
                })
              )
            }
            return data
          },
          setItem: (name, value) => {
            localStorage.setItem(name, JSON.stringify(value))
          },
          removeItem: (name) => {
            localStorage.removeItem(name)
          },
        },
        partialize: (state) => ({
          notifications: state.notifications,
          lastTriggered: state.lastTriggered,
          // latestNotification, toastQueue 제외 — 새로고침 시 stale 알림 방지
        } as unknown as NotificationState),
      }
    ),
    { name: 'notification-store' }
  )
)

// Selectors — 정보성 알림(backup, member-joined)은 배지 숫자에서 제외
const BADGE_EXCLUDE_TYPES: NotificationType[] = ['backup-reminder', 'member-joined']

const selectUnreadCount = (state: NotificationState) =>
  state.notifications.filter(
    (n) => !n.read && !BADGE_EXCLUDE_TYPES.includes(n.type)
  ).length

export function useUnreadCount() {
  return useNotificationStore(selectUnreadCount)
}
